import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity } from '../../utils/security';
import { toast } from 'react-toastify';
import {
    Users, Search, RefreshCw, ChevronRight,
    MessageSquare, FileText, Plus, Trash2,
    CheckCircle2, Clock, AlertCircle, Phone, Mail,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

export const getCustomerStatusCategory = (status) => {
    switch (status) {
        case 'contract_signed':
            return {
                key: 'green',
                label: 'კონტრაქტი გაფორმდა',
                color: 'success',
                borderColor: '#16a34a',
                bgGradient: 'linear-gradient(180deg, #f0fdf4 0%, #ffffff 50%)',
                badgeBg: '#dcfce7',
                badgeText: '#14532d',
                icon: <CheckCircle2 size={13} className="text-success" />
            };
        case 'negotiation':
        case 'meeting_scheduled':
            return {
                key: 'yellow',
                label: status === 'meeting_scheduled' ? 'შეხვედრა დაგეგმილია' : 'მოლაპარაკება',
                color: 'warning',
                borderColor: '#ca8a04',
                bgGradient: 'linear-gradient(180deg, #fefce8 0%, #ffffff 50%)',
                badgeBg: '#fef08a',
                badgeText: '#713f12',
                icon: <Clock size={13} className="text-warning" />
            };
        case 'offer_sent':
            return {
                key: 'blue',
                label: 'შეთავაზება გაგზავნილია',
                color: 'info',
                borderColor: '#2563eb',
                bgGradient: 'linear-gradient(180deg, #eff6ff 0%, #ffffff 50%)',
                badgeBg: '#dbeafe',
                badgeText: '#1e3a8a',
                icon: <Mail size={13} className="text-primary" />
            };
        default:
            return {
                key: 'red',
                label: status === 'rejected' ? 'უარყოფილია' : 'ლიდი / ადრეული ეტაპი',
                color: 'danger',
                borderColor: '#dc2626',
                bgGradient: 'linear-gradient(180deg, #fef2f2 0%, #ffffff 50%)',
                badgeBg: '#fee2e2',
                badgeText: '#7f1d1d',
                icon: <AlertCircle size={13} className="text-danger" />
            };
    }
};

const SECTOR_LABELS = {
    business: { label: 'კერძო ბიზნესი / Enterprise', icon: '🏢', badge: '#e0f2fe', color: '#0369a1' },
    gov: { label: 'საჯარო / სახელმწიფო სექტორი', icon: '🏛️', badge: '#fef3c7', color: '#b45309' },
    education: { label: 'განათლება & აკადემია', icon: '🎓', badge: '#ede9fe', color: '#6d28d9' },
    healthcare: { label: 'მედიცინა & ჯანდაცვა', icon: '🏥', badge: '#fce7f3', color: '#be185d' },
    fintech: { label: 'ფინტექი & ბანკინგი', icon: '💳', badge: '#dcfce7', color: '#15803d' },
    ngo: { label: 'არასამთავრობო / ფონდი', icon: '🌐', badge: '#ffedd5', color: '#c2410c' },
    other: { label: 'სხვა სფერო', icon: '📦', badge: '#f1f5f9', color: '#475569' }
};

const AdminPotentialCustomers = () => {
    const { user, isSuperAdmin, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const isKa = i18n.language === 'ka';

    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [filterSector, setFilterSector] = useState('all');
    const [filterPriority] = useState('all');

    // Add Modal State
    const [showAddModal, setShowAddModal] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newCustomer, setNewCustomer] = useState({
        name: '',
        sector: 'business',
        status: 'lead',
        priority: 'medium',
        contact_person: '',
        contact_position: '',
        contact_email: '',
        contact_phone: '',
        website: '',
        client_wants: '',
        our_offer: '',
        estimated_budget: '',
        notes: ''
    });

    useEffect(() => {
        if (!authLoading && !isSuperAdmin()) navigate('/admin');
    }, [authLoading, isSuperAdmin, navigate]);

    const fetchCustomers = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchWithSecurity(`${API_URL}customers.php`);
            const data = await res.json();
            setCustomers(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching potential customers:', err);
            toast.error(isKa ? 'კლიენტების ჩატვირთვა ვერ მოხერხდა' : 'Failed to load customers');
        }
        setLoading(false);
    }, [isKa]);

    useEffect(() => {
        if (user) fetchCustomers();
    }, [user, fetchCustomers]);

    // Handle Add Customer
    const handleAddCustomerSubmit = async (e) => {
        e.preventDefault();
        if (!newCustomer.name.trim()) {
            toast.error(isKa ? 'კლიენტის ან კომპანიის სახელი სავალდებულოა' : 'Name is required');
            return;
        }

        setCreating(true);
        try {
            const res = await fetchWithSecurity(`${API_URL}customers.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newCustomer)
            });
            const data = await res.json();
            if (data.success) {
                toast.success(isKa ? 'ახალი პოტენციური კლიენტი წარმატებით დაემატა!' : 'Customer added successfully!');
                setShowAddModal(false);
                setNewCustomer({
                    name: '',
                    sector: 'business',
                    status: 'lead',
                    priority: 'medium',
                    contact_person: '',
                    contact_position: '',
                    contact_email: '',
                    contact_phone: '',
                    website: '',
                    client_wants: '',
                    our_offer: '',
                    estimated_budget: '',
                    notes: ''
                });
                fetchCustomers();
                if (data.id) {
                    navigate(`/admin/potential-customers/${data.id}`);
                }
            } else {
                toast.error(data.error || (isKa ? 'დამატება ვერ მოხერხდა' : 'Failed to add'));
            }
        } catch (err) {
            toast.error(isKa ? 'დამატება ვერ მოხერხდა' : 'Failed to add');
        }
        setCreating(false);
    };

    const handleDelete = async (id, name, e) => {
        e.stopPropagation();
        if (!window.confirm(isKa ? `დარწმუნებული ხართ, რომ გსურთ წაშალოთ "${name}"?` : `Are you sure you want to delete "${name}"?`)) return;

        try {
            const res = await fetchWithSecurity(`${API_URL}customers.php?id=${id}`, {
                method: 'DELETE'
            });
            const data = await res.json();
            if (data.success) {
                toast.success(isKa ? 'კლიენტი წაიშალა' : 'Customer deleted');
                fetchCustomers();
            } else {
                toast.error(data.error || (isKa ? 'წაშლა ვერ მოხერხდა' : 'Failed to delete'));
            }
        } catch (err) {
            toast.error(isKa ? 'წაშლა ვერ მოხერხდა' : 'Failed to delete');
        }
    };

    // Filter Logic
    const filteredCustomers = useMemo(() => {
        return customers.filter(c => {
            const cat = getCustomerStatusCategory(c.status);
            if (filterCategory !== 'all' && cat.key !== filterCategory) return false;
            if (filterSector !== 'all' && c.sector !== filterSector) return false;
            if (filterPriority !== 'all' && c.priority !== filterPriority) return false;

            if (searchTerm.trim()) {
                const term = searchTerm.toLowerCase();
                const matchName = c.name?.toLowerCase().includes(term);
                const matchContact = c.contact_person?.toLowerCase().includes(term);
                const matchEmail = c.contact_email?.toLowerCase().includes(term);
                const matchPhone = c.contact_phone?.toLowerCase().includes(term);
                const matchWants = c.client_wants?.toLowerCase().includes(term);
                const matchOffer = c.our_offer?.toLowerCase().includes(term);
                if (!matchName && !matchContact && !matchEmail && !matchPhone && !matchWants && !matchOffer) {
                    return false;
                }
            }
            return true;
        });
    }, [customers, filterCategory, filterSector, filterPriority, searchTerm]);

    // Statistics
    const stats = useMemo(() => {
        const total = customers.length;
        const green = customers.filter(c => getCustomerStatusCategory(c.status).key === 'green').length;
        const yellow = customers.filter(c => getCustomerStatusCategory(c.status).key === 'yellow').length;
        const blue = customers.filter(c => getCustomerStatusCategory(c.status).key === 'blue').length;
        const red = customers.filter(c => getCustomerStatusCategory(c.status).key === 'red').length;
        const totalBudget = customers.reduce((sum, c) => sum + (parseFloat(c.estimated_budget) || 0), 0);
        return { total, green, yellow, blue, red, totalBudget };
    }, [customers]);

    if (authLoading || !user) return null;

    return (
        <AdminLayout title={isKa ? 'პოტენციური კლიენტები (Leads)' : 'Potential Customers & Leads'}>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <div>
                    <h4 className="fw-bold mb-1 d-flex align-items-center gap-2">
                        <Users size={22} className="text-primary" />
                        {isKa ? 'პოტენციური კლიენტების მართვა' : 'Potential Customers & B2B Leads'}
                    </h4>
                    <p className="text-muted small mb-0">
                        {isKa 
                            ? 'კლიენტის მოთხოვნები (რა სურს), Orbitronix-ის შეთავაზება, მოლაპარაკებების პროცესი და დეტალური პროფილები' 
                            : 'Track what clients want, our tailored proposals, communications and conversion pipeline'}
                    </p>
                </div>
                <div className="d-flex gap-2">
                    <button className="shadcn-btn shadcn-btn-secondary" onClick={fetchCustomers}>
                        <RefreshCw size={15} /> {isKa ? 'განახლება' : 'Refresh'}
                    </button>
                    <button className="shadcn-btn shadcn-btn-primary" onClick={() => navigate('/admin/potential-customers/new')}>
                        <Plus size={15} /> {isKa ? 'ახალი კლიენტის დამატება' : 'Add Potential Customer'}
                    </button>
                </div>
            </div>

            {/* Quick Stats Banner */}
            <div className="row g-3 mb-4">
                <div className="col-6 col-md-4 col-xl-2">
                    <div className="shadcn-card p-3 h-100">
                        <div className="text-muted smallest fw-semibold mb-1">{isKa ? 'სულ კლიენტები' : 'Total Leads'}</div>
                        <div className="fs-4 fw-bold text-dark">{stats.total}</div>
                    </div>
                </div>
                <div className="col-6 col-md-4 col-xl-2">
                    <div className="shadcn-card p-3 h-100" style={{ borderLeft: '4px solid #16a34a' }}>
                        <div className="text-success smallest fw-semibold mb-1">{isKa ? '🟢 კონტრაქტი გაფორმდა' : '🟢 Contract Signed'}</div>
                        <div className="fs-4 fw-bold text-success">{stats.green}</div>
                    </div>
                </div>
                <div className="col-6 col-md-4 col-xl-2">
                    <div className="shadcn-card p-3 h-100" style={{ borderLeft: '4px solid #ca8a04' }}>
                        <div className="text-warning smallest fw-semibold mb-1">{isKa ? '🟡 მოლაპარაკებაში' : '🟡 In Negotiation'}</div>
                        <div className="fs-4 fw-bold text-warning">{stats.yellow}</div>
                    </div>
                </div>
                <div className="col-6 col-md-4 col-xl-2">
                    <div className="shadcn-card p-3 h-100" style={{ borderLeft: '4px solid #2563eb' }}>
                        <div className="text-primary smallest fw-semibold mb-1">{isKa ? '🔵 შეთავაზება გაგზავნილია' : '🔵 Offer Sent'}</div>
                        <div className="fs-4 fw-bold text-primary">{stats.blue}</div>
                    </div>
                </div>
                <div className="col-6 col-md-4 col-xl-2">
                    <div className="shadcn-card p-3 h-100" style={{ borderLeft: '4px solid #dc2626' }}>
                        <div className="text-danger smallest fw-semibold mb-1">{isKa ? '🔴 ლიდი / დასაწყისი' : '🔴 Early Lead'}</div>
                        <div className="fs-4 fw-bold text-danger">{stats.red}</div>
                    </div>
                </div>
                <div className="col-12 col-md-4 col-xl-2">
                    <div className="shadcn-card p-3 h-100" style={{ background: 'linear-gradient(135deg, #1e1b4b, #312e81)', color: '#fff', border: 'none' }}>
                        <div className="smallest opacity-75 fw-semibold mb-1">{isKa ? 'მთლიანი Pipeline ბიუჯეტი' : 'Pipeline Budget'}</div>
                        <div className="fs-5 fw-bold text-white">₾ {stats.totalBudget.toLocaleString()}</div>
                    </div>
                </div>
            </div>

            {/* Filters Bar */}
            <div className="shadcn-card p-3 mb-4">
                <div className="row g-3 align-items-center">
                    <div className="col-lg-6">
                        <div className="d-flex gap-2 flex-wrap">
                            <button
                                className={`shadcn-btn shadcn-btn-sm ${filterCategory === 'all' ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                                onClick={() => setFilterCategory('all')}
                            >
                                {isKa ? 'ყველა' : 'All'} ({stats.total})
                            </button>
                            <button
                                className={`shadcn-btn shadcn-btn-sm ${filterCategory === 'green' ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                                onClick={() => setFilterCategory('green')}
                            >
                                🟢 {isKa ? 'კონტრაქტი' : 'Won'} ({stats.green})
                            </button>
                            <button
                                className={`shadcn-btn shadcn-btn-sm ${filterCategory === 'yellow' ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                                onClick={() => setFilterCategory('yellow')}
                            >
                                🟡 {isKa ? 'მოლაპარაკება' : 'Working'} ({stats.yellow})
                            </button>
                            <button
                                className={`shadcn-btn shadcn-btn-sm ${filterCategory === 'blue' ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                                onClick={() => setFilterCategory('blue')}
                            >
                                🔵 {isKa ? 'შეთავაზება' : 'Offer Sent'} ({stats.blue})
                            </button>
                            <button
                                className={`shadcn-btn shadcn-btn-sm ${filterCategory === 'red' ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                                onClick={() => setFilterCategory('red')}
                            >
                                🔴 {isKa ? 'ლიდი' : 'Leads'} ({stats.red})
                            </button>
                        </div>
                    </div>

                    <div className="col-sm-6 col-lg-3">
                        <select
                            className="shadcn-input form-select-sm"
                            value={filterSector}
                            onChange={e => setFilterSector(e.target.value)}
                        >
                            <option value="all">{isKa ? 'ყველა სექტორი' : 'All Sectors'}</option>
                            {Object.entries(SECTOR_LABELS).map(([k, v]) => (
                                <option key={k} value={k}>{v.icon} {v.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="col-sm-6 col-lg-3">
                        <div className="position-relative">
                            <Search size={16} className="position-absolute text-muted" style={{ left: '12px', top: '10px' }} />
                            <input
                                type="text"
                                className="shadcn-input ps-5"
                                placeholder={isKa ? 'ძებნა კლიენტით, მოთხოვნით...' : 'Search by name, wants...'}
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Potential Customer Cards Grid */}
            {loading ? (
                <div className="text-center py-5 text-muted">იტვირთება...</div>
            ) : filteredCustomers.length === 0 ? (
                <div className="shadcn-card text-center py-5">
                    <Users size={48} className="text-muted mb-3 opacity-50" />
                    <h5 className="fw-bold">{isKa ? 'პოტენციური კლიენტები არ მოიძებნა' : 'No customers found'}</h5>
                    <p className="text-muted small">
                        {isKa ? 'დაამატეთ ახალი პოტენციური კლიენტი ზედა ღილაკით' : 'Add a new potential customer using the button above'}
                    </p>
                </div>
            ) : (
                <div className="row g-4">
                    {filteredCustomers.map(cust => {
                        const statusCat = getCustomerStatusCategory(cust.status);
                        const sectorInfo = SECTOR_LABELS[cust.sector] || SECTOR_LABELS.other;

                        return (
                            <div className="col-md-6 col-xl-4" key={cust.id}>
                                <div 
                                    className="shadcn-card h-100 d-flex flex-column justify-content-between p-4 position-relative hover-shadow cursor-pointer"
                                    onClick={() => navigate(`/admin/potential-customers/${cust.id}`)}
                                    style={{
                                        borderLeft: `5px solid ${statusCat.borderColor}`,
                                        background: statusCat.bgGradient,
                                        transition: 'all 0.2s ease',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <div>
                                        {/* Card Top: Sector Badge & Status */}
                                        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-1">
                                            <span 
                                                className="badge rounded-pill px-2 py-1 smallest fw-semibold"
                                                style={{ background: sectorInfo.badge, color: sectorInfo.color }}
                                            >
                                                {sectorInfo.icon} {sectorInfo.label}
                                            </span>
                                            <span 
                                                className="badge rounded-pill px-2 py-1 smallest fw-semibold"
                                                style={{ background: statusCat.badgeBg, color: statusCat.badgeText }}
                                            >
                                                {statusCat.label}
                                            </span>
                                        </div>

                                        {/* Title & Budget */}
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <h5 className="fw-bold text-dark mb-0 text-truncate me-2" title={cust.name}>
                                                {cust.name}
                                            </h5>
                                            {parseFloat(cust.estimated_budget) > 0 && (
                                                <span className="badge bg-dark text-white rounded-pill px-2 py-1 smallest font-monospace flex-shrink-0">
                                                    ₾ {parseFloat(cust.estimated_budget).toLocaleString()}
                                                </span>
                                            )}
                                        </div>

                                        {/* What Customer Wants */}
                                        {cust.client_wants && (
                                            <div className="p-2 rounded mb-2" style={{ background: 'rgba(0,0,0,0.03)', borderLeft: '3px solid #6366f1' }}>
                                                <div className="text-muted smallest fw-bold text-uppercase mb-1" style={{ fontSize: '10px' }}>
                                                    🎯 {isKa ? 'რა სურს კლიენტს (What he wants):' : 'What client wants:'}
                                                </div>
                                                <p className="smallest text-dark mb-0 text-truncate-2" style={{ maxHeight: '42px', overflow: 'hidden' }}>
                                                    {cust.client_wants}
                                                </p>
                                            </div>
                                        )}

                                        {/* What We Offer */}
                                        {cust.our_offer && (
                                            <div className="p-2 rounded mb-3" style={{ background: 'rgba(0,0,0,0.03)', borderLeft: '3px solid #059669' }}>
                                                <div className="text-muted smallest fw-bold text-uppercase mb-1" style={{ fontSize: '10px' }}>
                                                    💼 {isKa ? 'ჩვენი შეთავაზება (Our offer):' : 'What we offer:'}
                                                </div>
                                                <p className="smallest text-dark mb-0 text-truncate-2" style={{ maxHeight: '42px', overflow: 'hidden' }}>
                                                    {cust.our_offer}
                                                </p>
                                            </div>
                                        )}

                                        {/* Contact Person Details */}
                                        <div className="d-flex flex-column gap-1 smallest text-muted mb-3">
                                            {cust.contact_person && (
                                                <div className="d-flex align-items-center gap-2 text-dark fw-medium">
                                                    <span>👤 {cust.contact_person}</span>
                                                    {cust.contact_position && <span className="text-muted smallest">({cust.contact_position})</span>}
                                                </div>
                                            )}
                                            {cust.contact_phone && (
                                                <a href={`tel:${cust.contact_phone}`} onClick={e => e.stopPropagation()} className="text-decoration-none text-muted d-flex align-items-center gap-1">
                                                    <Phone size={12} className="text-success" /> {cust.contact_phone}
                                                </a>
                                            )}
                                            {cust.contact_email && (
                                                <a href={`mailto:${cust.contact_email}`} onClick={e => e.stopPropagation()} className="text-decoration-none text-muted d-flex align-items-center gap-1">
                                                    <Mail size={12} className="text-info" /> {cust.contact_email}
                                                </a>
                                            )}
                                        </div>
                                    </div>

                                    {/* Footer Stats & Actions */}
                                    <div className="pt-2 border-top d-flex justify-content-between align-items-center">
                                        <div className="d-flex gap-3 text-muted smallest">
                                            <span title={isKa ? 'შეტყობინებები' : 'Messages'} className="d-flex align-items-center gap-1">
                                                <MessageSquare size={13} /> {cust.comm_count || 0}
                                            </span>
                                            <span title={isKa ? 'კონტაქტები' : 'Contacts'} className="d-flex align-items-center gap-1">
                                                <Users size={13} /> {cust.contacts_count || 0}
                                            </span>
                                            <span title={isKa ? 'ფაილები' : 'Files'} className="d-flex align-items-center gap-1">
                                                <FileText size={13} /> {cust.files_count || 0}
                                            </span>
                                        </div>

                                        <div className="d-flex gap-1" onClick={e => e.stopPropagation()}>
                                            <button
                                                type="button"
                                                className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm"
                                                onClick={() => navigate(`/admin/potential-customers/${cust.id}`)}
                                                title={isKa ? 'დეტალების მართვა' : 'Manage details'}
                                            >
                                                <span>{isKa ? 'მართვა' : 'Manage'}</span>
                                                <ChevronRight size={14} />
                                            </button>
                                            <button
                                                type="button"
                                                className="shadcn-btn shadcn-btn-danger shadcn-btn-sm"
                                                onClick={e => handleDelete(cust.id, cust.name, e)}
                                                title={isKa ? 'წაშლა' : 'Delete'}
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ── ADD POTENTIAL CUSTOMER MODAL ── */}
            {showAddModal && (
                <div 
                    className="modal fade show d-block" 
                    tabIndex="-1" 
                    style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', zIndex: 1050 }}
                >
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content border-0 shadow-xl" style={{ borderRadius: '16px' }}>
                            <div className="modal-header border-bottom p-3">
                                <h5 className="modal-title fw-bold fs-6 d-flex align-items-center gap-2">
                                    <Plus size={18} className="text-primary" />
                                    {isKa ? 'ახალი პოტენციური კლიენტის დამატება' : 'Add New Potential Customer'}
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowAddModal(false)} />
                            </div>
                            <form onSubmit={handleAddCustomerSubmit}>
                                <div className="modal-body p-4" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                                    <div className="row g-3">
                                        <div className="col-md-7">
                                            <label className="form-label small fw-bold">{isKa ? 'კომპანიის / კლიენტის სახელი' : 'Company / Client Name'} *</label>
                                            <input
                                                type="text"
                                                className="shadcn-input"
                                                placeholder={isKa ? 'მაგ: საქართველოს ლოგისტიკური ჰაბი' : 'e.g. Georgian Logistics Hub'}
                                                value={newCustomer.name}
                                                onChange={e => setNewCustomer({ ...newCustomer, name: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-5">
                                            <label className="form-label small fw-bold">{isKa ? 'სექტორი / ინდუსტრია' : 'Sector'} *</label>
                                            <select
                                                className="shadcn-input"
                                                value={newCustomer.sector}
                                                onChange={e => setNewCustomer({ ...newCustomer, sector: e.target.value })}
                                            >
                                                {Object.entries(SECTOR_LABELS).map(([k, v]) => (
                                                    <option key={k} value={k}>{v.icon} {v.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label small fw-bold">{isKa ? 'საწყისი სტატუსი' : 'Initial Status'}</label>
                                            <select
                                                className="shadcn-input"
                                                value={newCustomer.status}
                                                onChange={e => setNewCustomer({ ...newCustomer, status: e.target.value })}
                                            >
                                                <option value="lead">🔴 ლიდი / დასაწყისი</option>
                                                <option value="meeting_scheduled">🟡 შეხვედრა დაგეგმილია</option>
                                                <option value="negotiation">🟡 მოლაპარაკება</option>
                                                <option value="offer_sent">🔵 შეთავაზება გაგზავნილია</option>
                                                <option value="contract_signed">🟢 კონტრაქტი გაფორმდა</option>
                                            </select>
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label small fw-bold">{isKa ? 'პრიორიტეტი' : 'Priority'}</label>
                                            <select
                                                className="shadcn-input"
                                                value={newCustomer.priority}
                                                onChange={e => setNewCustomer({ ...newCustomer, priority: e.target.value })}
                                            >
                                                <option value="low">დაბალი (Low)</option>
                                                <option value="medium">საშუალო (Medium)</option>
                                                <option value="high">მაღალი (High)</option>
                                                <option value="urgent">გადაუდებელი (Urgent)</option>
                                            </select>
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label small fw-bold">{isKa ? 'საორიენტაციო ბიუჯეტი (₾)' : 'Estimated Budget (GEL)'}</label>
                                            <input
                                                type="number"
                                                className="shadcn-input"
                                                placeholder="მაგ: 35000"
                                                value={newCustomer.estimated_budget}
                                                onChange={e => setNewCustomer({ ...newCustomer, estimated_budget: e.target.value })}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label small fw-bold">{isKa ? 'საკონტაქტო პირი' : 'Contact Person'}</label>
                                            <input
                                                type="text"
                                                className="shadcn-input"
                                                placeholder="სახელი და გვარი"
                                                value={newCustomer.contact_person}
                                                onChange={e => setNewCustomer({ ...newCustomer, contact_person: e.target.value })}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label small fw-bold">{isKa ? 'თანამდებობა' : 'Position'}</label>
                                            <input
                                                type="text"
                                                className="shadcn-input"
                                                placeholder="მაგ: IT დირექტორი, მენეჯერი"
                                                value={newCustomer.contact_position}
                                                onChange={e => setNewCustomer({ ...newCustomer, contact_position: e.target.value })}
                                            />
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label small fw-bold">{isKa ? 'ელ-ფოსტა' : 'Email'}</label>
                                            <input
                                                type="email"
                                                className="shadcn-input"
                                                placeholder="contact@company.ge"
                                                value={newCustomer.contact_email}
                                                onChange={e => setNewCustomer({ ...newCustomer, contact_email: e.target.value })}
                                            />
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label small fw-bold">{isKa ? 'ტელეფონი' : 'Phone'}</label>
                                            <input
                                                type="text"
                                                className="shadcn-input"
                                                placeholder="+995 ..."
                                                value={newCustomer.contact_phone}
                                                onChange={e => setNewCustomer({ ...newCustomer, contact_phone: e.target.value })}
                                            />
                                        </div>

                                        <div className="col-md-4">
                                            <label className="form-label small fw-bold">{isKa ? 'ვებსაიტი' : 'Website'}</label>
                                            <input
                                                type="url"
                                                className="shadcn-input"
                                                placeholder="https://..."
                                                value={newCustomer.website}
                                                onChange={e => setNewCustomer({ ...newCustomer, website: e.target.value })}
                                            />
                                        </div>

                                        {/* What Customer Wants */}
                                        <div className="col-12">
                                            <label className="form-label small fw-bold text-primary">
                                                🎯 {isKa ? 'რა სურს კლიენტს (What he wants / მოთხოვნები & საჭიროებები)' : 'What client wants (Needs & Requirements)'}
                                            </label>
                                            <textarea
                                                className="shadcn-input"
                                                rows="3"
                                                placeholder={isKa ? 'რა პრობლემების გადაჭრა სურთ, რა მოდულები და ფუნქციონალი აინტერესებთ...' : 'Describe what the client is looking for...'}
                                                value={newCustomer.client_wants}
                                                onChange={e => setNewCustomer({ ...newCustomer, client_wants: e.target.value })}
                                            />
                                        </div>

                                        {/* What We Offer */}
                                        <div className="col-12">
                                            <label className="form-label small fw-bold text-success">
                                                💼 {isKa ? 'რას ვთავაზობთ (What we offer / ჩვენი გადაწყვეტა)' : 'What we offer (Orbitronix Solution)'}
                                            </label>
                                            <textarea
                                                className="shadcn-input"
                                                rows="3"
                                                placeholder={isKa ? 'ჩვენი შეთავაზებული პაკეტი, ტექნოლოგიური სტეკი, სერვისები...' : 'Proposed solution, products and package...'}
                                                value={newCustomer.our_offer}
                                                onChange={e => setNewCustomer({ ...newCustomer, our_offer: e.target.value })}
                                            />
                                        </div>

                                        <div className="col-12">
                                            <label className="form-label small fw-bold">{isKa ? 'შიდა შენიშვნები' : 'Internal Notes'}</label>
                                            <textarea
                                                className="shadcn-input"
                                                rows="2"
                                                placeholder={isKa ? 'დამატებითი დეტალები გუნდისთვის...' : 'Additional notes for team...'}
                                                value={newCustomer.notes}
                                                onChange={e => setNewCustomer({ ...newCustomer, notes: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer border-top p-3">
                                    <button type="button" className="shadcn-btn shadcn-btn-secondary" onClick={() => setShowAddModal(false)}>
                                        {isKa ? 'გაუქმება' : 'Cancel'}
                                    </button>
                                    <button type="submit" className="shadcn-btn shadcn-btn-primary" disabled={creating}>
                                        {creating ? (isKa ? 'ემატება...' : 'Adding...') : (isKa ? 'კლიენტის შექმნა & გახსნა' : 'Create & Open Details')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default AdminPotentialCustomers;
